(() => {
  'use strict';
  if (window.AUPurchase) return;
  const forms = new WeakMap();
  const contexts = new WeakMap();
  const registered = new Set();
  const lockedStatuses = new Set(['pending', 'reconciling', 'confirmed', 'unknown']);
  let activeOperation = null;
  let activeReview = null;
  let openChooser = null;
  let sequence = 0;
  const root = () => window.Shopify?.routes?.root || window.theme?.routes?.root || '/';
  const nativeInfo = (form) => form.closest('product-info, [data-au-purchase-native]');
  const route = (path) => new URL(`${root()}${path}`, location.origin).href;
  const money = (cents) => {
    if (!Number.isFinite(Number(cents))) return '';
    if (window.theme?.formatMoney) { const span = document.createElement('span'); span.innerHTML = window.theme.formatMoney(Number(cents), window.theme.moneyFormat); return span.textContent; }
    const currency = window.Shopify?.currency?.active || document.querySelector('[data-au-purchase-config]') && config(document.querySelector('[data-au-purchase]')).currency;
    if (!currency) return '';
    return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency }).format(Number(cents) / 100);
  };
  const json = (node) => { try { return JSON.parse(node?.textContent || '{}'); } catch (_) { return {}; } };
  // Release 6.9 (spec 27, 4.5): the product page Pix line per variant, from the
  // map au-r62-buy renders next to it (server rendered, one entry per variant).
  const pixMaps = new Map();
  const swapPix = (productId, variantId) => {
    const id = String(productId || '');
    if (!/^\d+$/.test(id)) return;
    const slot = document.querySelector(`[data-au-r69-pix-slot][data-product-id="${id}"]`);
    const source = document.querySelector(`script[data-au-r69-pix-map][data-product-id="${id}"]`);
    if (!slot || !source) return;
    if (!pixMaps.has(id)) pixMaps.set(id, json(source));
    const html = pixMaps.get(id)[String(variantId)] || '';
    if (slot.innerHTML !== html) slot.innerHTML = html;
  };
  const config = (context) => context ? json(context.querySelector('[data-au-purchase-config]')) : {};
  const labelsFor = (form) => {
    const record = forms.get(form);
    return record?.config.labels || json(document.querySelector('[data-au-purchase-labels]')) || {};
  };
  const label = (form, key) => labelsFor(form)[key] || '';
  const setText = (node, value) => {
    if (!node || node.textContent === String(value ?? '')) return;
    if (node.childNodes.length === 1 && node.firstChild.nodeType === Node.TEXT_NODE) node.firstChild.data = String(value ?? '');
    else node.textContent = value ?? '';
  };
  // Keep price nodes stable during quantity changes. Create structure only when
  // the sale state actually changes, not on every selection or pending update.
  const priceText = (node, current, previous = '') => {
    const hasPrevious = Boolean(previous);
    const first = node.firstChild;
    const strike = node.querySelector('s');
    if (first?.nodeType !== Node.TEXT_NODE || Boolean(strike) !== hasPrevious) {
      node.replaceChildren(document.createTextNode(current));
      if (hasPrevious) { const comparison = document.createElement('s'); comparison.textContent = previous; node.append(document.createTextNode(' '), comparison); }
      return;
    }
    if (first.data !== current) first.data = current;
    if (strike) setText(strike, previous);
  };
  const setAttribute = (node, name, value) => { if (node && node.getAttribute(name) !== String(value)) node.setAttribute(name, String(value)); };
  const setProperty = (node, name, value) => { if (node && node[name] !== value) node[name] = value; };
  const safeEvent = (target, name, detail) => {
    try { target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail })); } catch (_) { /* Optional consumers cannot change the cart outcome. */ }
  };
  // Read the first successful selection control without serializing the entire
  // form (or dispatching formdata) for each state read. The mutation payload
  // still uses FormData so line properties retain their native contract.
  const formValue = (form, name) => {
    for (const control of form.elements) {
      if (control.name !== name || control.matches(':disabled')) continue;
      if (control instanceof HTMLInputElement) {
        if (['button', 'submit', 'reset', 'image', 'file'].includes(control.type)) continue;
        if (['radio', 'checkbox'].includes(control.type) && !control.checked) continue;
        return control.value;
      }
      if (control instanceof HTMLSelectElement) {
        const option = [...control.options].find((item) => item.selected && !item.disabled && !item.closest('optgroup[disabled]'));
        if (option) return option.value;
      }
      if (control instanceof HTMLTextAreaElement) return control.value;
    }
    return null;
  };
  const submitButtons = (form) => [...form.elements].filter((element) => element instanceof HTMLButtonElement && element.type === 'submit');
  const variantFor = (form, id) => {
    const record = forms.get(form);
    const native = json(nativeInfo(form)?.querySelector('[data-selected-variant]'));
    const stored = record?.config.variants?.find((item) => String(item.id) === String(id));
    const current = String(native.id) === String(id) ? { ...stored, ...native } : stored;
    return record?.fresh?.id === String(id) ? { ...current, ...record.fresh.variant } : current;
  };
  const liveSelection = (form) => {
    const record = forms.get(form);
    const id = String(formValue(form, 'id') || '');
    const variant = variantFor(form, id);
    const quantityInput = [...form.elements].find((input) => input.name === 'quantity');
    const quantity = Number(formValue(form, 'quantity') || 1);
    const planId = String(formValue(form, 'selling_plan') || '');
    const allocation = (variant?.selling_plan_allocations || []).find((item) => String(item.selling_plan_id || item.selling_plan?.id) === planId);
    const planValid = !planId ? !variant?.requires_selling_plan && !record?.config.requiresSellingPlan : Boolean(allocation);
    const rule = variant?.quantity_rule || {};
    const minimum = Number(quantityInput?.min || rule.min || 1);
    const maximum = Number(quantityInput?.max || rule.max || Infinity);
    const increment = Number(quantityInput?.step || rule.increment || 1);
    const quantityValid = Number.isInteger(quantity) && quantity >= minimum && quantity <= maximum && (quantity - minimum) % increment === 0;
    const complete = Boolean(id && variant && planValid && !record?.requiresChoice && !record?.pendingVariant);
    const nativeButton = form.querySelector('[data-add-to-cart]');
    const disabledByNative = Boolean(nativeButton?.disabled && !record?.disabledByUs.has(nativeButton));
    const available = Boolean(complete && variant.available && quantityValid && !disabledByNative && record?.context?.dataset.auSuppressed !== 'true' && !['out_of_stock', 'preorder'].includes(variant.commerceState));
    const unitPrice = Number(planId ? allocation?.price : variant?.price);
    return { status: record?.status || 'idle', productId: record?.config.productId || Number(form.closest('product-info')?.dataset.productId), variantId: complete ? id : null, quantity, sellingPlanId: planId || null, planName: allocation?.selling_plan?.name || null, unitPrice: Number.isFinite(unitPrice) ? unitPrice : null, totalPrice: Number.isFinite(unitPrice) && quantityValid ? unitPrice * quantity : null, available, selectionComplete: complete, quantityValid, locked: lockedStatuses.has(record?.status), variant, formId: form.getAttribute('id') };
  };
  const selection = (form) => {
    const record = forms.get(form);
    return record?.frozen && lockedStatuses.has(record.status) ? { ...record.frozen, status: record.status, locked: true } : liveSelection(form);
  };
  const publicState = (state) => {
    const { variant, ...safe } = state;
    return Object.freeze(safe);
  };
  const statusNode = (form) => {
    const record = forms.get(form);
    let node = form.closest('[data-au-purchase-modal]') ? form.querySelector('[data-au-purchase-status]') : record.context?.querySelector('[data-au-purchase-status]') || form.querySelector('[data-au-purchase-status]');
    if (!node) {
      node = document.createElement('p');
      node.className = 'au-purchase__status';
      node.dataset.auPurchaseStatus = '';
      node.setAttribute('role', 'status');
      node.setAttribute('aria-live', 'polite');
      node.setAttribute('aria-atomic', 'true');
      form.append(node);
    }
    return node;
  };
  const recovery = (form, visible) => {
    const record = forms.get(form);
    let node = form.closest('[data-au-purchase-modal]') ? form.querySelector('[data-au-purchase-recovery]') : record.context?.querySelector('[data-au-purchase-recovery]') || form.querySelector('[data-au-purchase-recovery]');
    if (!node && visible) {
      node = document.createElement('div');
      node.className = 'au-purchase__recovery';
      node.dataset.auPurchaseRecovery = '';
      const link = document.createElement('a');
      link.href = route('cart');
      link.textContent = label(form, 'cart');
      const button = document.createElement('button');
      button.type = 'button';
      button.dataset.auPurchaseReconcile = '';
      button.textContent = label(form, 'review');
      node.append(link, button);
      form.append(node);
    }
    setProperty(node, 'hidden', !visible);
  };
  const render = (form, message) => {
    const record = forms.get(form);
    if (!record) return;
    const state = selection(form);
    for (const button of submitButtons(form)) {
      const shouldDisable = state.locked || !state.available;
      if (shouldDisable && !button.disabled) { record.disabledByUs.add(button); button.disabled = true; }
      if (!shouldDisable && record.disabledByUs.has(button)) { button.disabled = false; record.disabledByUs.delete(button); }
      setAttribute(button, 'aria-busy', ['pending', 'reconciling'].includes(state.status));
    }
    setAttribute(form, 'data-au-purchase-state', state.status);
    setAttribute(form, 'aria-busy', ['pending', 'reconciling'].includes(state.status));
    const target = record.context || form;
    const displayLabel = state.locked ? label(form, state.status === 'confirmed' ? 'added' : state.status) : !state.selectionComplete ? label(form, 'choose_variant') : state.available ? label(form, 'add') : label(form, 'unavailable');
    setText(target.querySelector('[data-au-purchase-label]'), displayLabel);
    const totalNode = target.querySelector('[data-au-purchase-total]');
    const totalSignature = `${state.variantId}:${state.totalPrice}:${state.selectionComplete}`;
    if (totalNode && totalNode.dataset.auPriceSignature !== totalSignature) {
      setText(totalNode, state.selectionComplete ? money(state.totalPrice) : '');
      totalNode.dataset.auPriceSignature = totalSignature;
    }
    const totalRow = target.querySelector('[data-au-purchase-total-row]');
    setProperty(totalRow, 'hidden', !state.selectionComplete || state.totalPrice === null || state.quantity <= 1);
    const modal = form.closest('[data-au-purchase-modal]');
    if (modal) {
      const nativeLabel = form.querySelector('[data-add-to-cart-text]');
      const textNode = [...nativeLabel?.childNodes || []].find((node) => node.nodeType === Node.TEXT_NODE);
      if (textNode && textNode.textContent !== `${displayLabel} `) textNode.textContent = `${displayLabel} `;
      for (const choice of modal.querySelectorAll('[data-popout-option]')) {
        const variant = record.config.variants?.find((item) => item.title === choice.dataset.value);
        if (!variant?.format) continue;
        setText(choice.querySelector('span') || choice, variant.format);
        if (choice.getAttribute('aria-current') === 'true') setText(choice.closest('popout-select')?.querySelector('[data-popout-toggle-text]'), variant.format);
      }
    }
    const nativeTotal = form.querySelector('[data-product-price]');
    if (nativeTotal) {
      const signature = `${state.variantId}:${state.quantity}:${state.totalPrice}:${state.variant?.compare_at_price}:${state.sellingPlanId}`;
      if (nativeTotal.dataset.auPriceSignature !== signature) {
        priceText(nativeTotal, state.selectionComplete ? money(state.totalPrice) : '', state.selectionComplete && !state.sellingPlanId && !record.config.isKit && state.variant.compare_at_price > state.unitPrice ? money(state.variant.compare_at_price * state.quantity) : '');
        nativeTotal.dataset.auPriceSignature = signature;
      }
    }
    // Release 4, G.01: the buy button's name says the action and the price, from data.
    const pricedAdd = !state.locked && state.selectionComplete && state.available && state.totalPrice !== null;
    for (const button of submitButtons(form)) {
      const template = button.dataset.auNameTemplate;
      if (!template) continue;
      const shown = button.querySelector('[data-au-purchase-total], [data-product-price]');
      const labelNode = button.querySelector('[data-au-purchase-label]') || button.querySelector('[data-add-to-cart-text]');
      const visibleLabel = (labelNode?.childNodes[0]?.textContent || '').trim();
      const name = pricedAdd && shown && visibleLabel === label(form, 'add') ? template.replace('[[price]]', money(state.totalPrice)) : '';
      if (name) { if (button.getAttribute('aria-label') !== name) button.setAttribute('aria-label', name); }
      else button.removeAttribute('aria-label');
    }
    if (!record.context && record.fresh) {
      const info = nativeInfo(form);
      setText(info?.querySelector('.av53-price__selling'), money(state.unitPrice));
      if (record.fresh.variant.unitHtml === '') info?.querySelectorAll('[data-av-unit-price], [data-av-portion-price]').forEach((node) => { node.hidden = true; });
    }
    if (state.variant) {
      const primary = target.querySelector('[data-au-purchase-price]');
      if (primary) {
        const signature = `${state.variantId}:${state.unitPrice}:${state.variant.compare_at_price}:${state.sellingPlanId}`;
        if (primary.dataset.auPriceSignature !== signature) {
          priceText(primary, money(state.unitPrice), !state.sellingPlanId && !record.config.isKit && state.variant.compare_at_price > state.unitPrice ? money(state.variant.compare_at_price) : '');
          primary.dataset.auPriceSignature = signature;
        }
      }
      setText(target.querySelector('[data-au-purchase-format]'), state.variant.format || '');
      const unit = target.querySelector('[data-au-purchase-unit]');
      if (unit && unit.innerHTML !== (state.variant.unitHtml || '')) unit.innerHTML = state.variant.unitHtml || '';
      const image = record.context?.closest('[data-au-product-card], [data-spoon-panel]')?.querySelector('[data-au-purchase-image]');
      if (image && state.variant.image && record.lastVariantId !== state.variantId) {
        image.removeAttribute('srcset'); image.src = state.variant.image; image.alt = state.variant.imageAlt || record.config.title; image.dataset.auVariantImage = state.variant.image;
      }
    } else {
      setText(target.querySelector('[data-au-purchase-format]'), '');
      const unit = target.querySelector('[data-au-purchase-unit]');
      if (unit) unit.replaceChildren();
      const prices = (record.config.variants || []).filter((variant) => variant.available && !variant.requires_selling_plan).map((variant) => variant.price);
      const minimum = prices.length ? Math.min(...prices) : null;
      const maximum = prices.length ? Math.max(...prices) : null;
      const primary = target.querySelector('[data-au-purchase-price]');
      if (primary) delete primary.dataset.auPriceSignature;
      setText(primary, minimum === null ? '' : minimum === maximum ? money(minimum) : (record.config.labels?.from_price || '').replace('__AU_PRICE__', money(minimum)).replace('{{ price }}', money(minimum)));
    }
    if (!record.context && record.lastVariantId !== state.variantId) swapPix(record.config.productId, state.variantId || '');
    record.lastVariantId = state.variantId;
    if (message !== undefined) setText(statusNode(form), message);
    recovery(form, ['unknown', 'confirmed'].includes(record.status));
    safeEvent(form, 'australia:purchase_state', publicState(state));
    if (record.context && !form.isConnected) safeEvent(record.context, 'australia:purchase_state', publicState(state));
  };
  const setStatus = (form, status, message) => {
    const record = forms.get(form); if (!record) return;
    record.status = status;
    if (!lockedStatuses.has(status)) {
      record.frozen = null;
      for (const [control, wasInert] of record.frozenControls) control.inert = wasInert;
      record.frozenControls.clear();
    }
    render(form, message === undefined ? label(form, status) : message);
  };
  function register(form, suppliedContext) {
    if (!(form instanceof HTMLFormElement)) return null;
    if (forms.has(form)) return publicState(selection(form));
    const context = suppliedContext || form.closest('[data-au-purchase]') || document.getElementById(form.dataset.auPurchaseContext || '');
    const info = nativeInfo(form);
    const stored = { ...json(info?.querySelector('[data-au-purchase-native-config]')), ...config(context) };
    const nativeLabels = json(document.querySelector('[data-au-purchase-labels]'));
    const settings = { ...stored, labels: stored.labels || nativeLabels, productId: stored.productId || Number(info?.dataset.productId), handle: stored.handle || info?.dataset.productHandle, url: stored.url || info?.dataset.url, title: stored.title || info?.querySelector('h1,h2,.product__title')?.textContent?.trim(), currency: stored.currency || window.Shopify?.currency?.active };
    forms.set(form, { context, config: settings, status: 'idle', disabledByUs: new WeakSet(), fresh: null, operation: null, frozen: null, frozenControls: new Map(), lastVariantId: String(formValue(form, 'id') || '') || null });
    registered.add(form);
    form.dataset.auPurchaseForm = '';
    if (context) contexts.set(context, { ...contexts.get(context), form });
    form.addEventListener('change', (event) => {
      const record = forms.get(form);
      if (!record || lockedStatuses.has(record.status)) { render(form); return; }
      if (event.target.name === 'id') record.pendingVariant = false;
      setStatus(form, 'idle', '');
    });
    if (info && 'MutationObserver' in window) {
      let queued = false;
      const selectionSelector = '[data-selected-variant], variant-selects, input[name="id"], input[name="quantity"], input[name="selling_plan"], select[name="selling_plan"], [data-add-to-cart]';
      const observer = new MutationObserver((mutations) => {
        const relevant = mutations.some((mutation) => {
          const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
          if (mutation.type === 'attributes') return target?.form === form && (['id', 'quantity', 'selling_plan'].includes(target.name) || target.matches('[data-add-to-cart]'));
          if (target?.closest('[data-selected-variant], variant-selects')) return true;
          // Ignore our price/status/bar output. Native option/variant or submit
          // replacement still refreshes the authoritative form state.
          return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => node instanceof Element && (node.matches(selectionSelector) || node.querySelector(selectionSelector)));
        });
        if (!relevant || queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; if (form.isConnected) render(form); });
      });
      observer.observe(info, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'value'] });
      forms.get(form).observer = observer;
    }
    render(form);
    return publicState(selection(form));
  }
  async function requestJson(url, options = {}) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal, headers: { Accept: 'application/json', ...options.headers } });
      let body = null;
      try { body = await response.json(); } catch (_) { /* An unreadable mutation response must be reconciled. */ }
      return { response, body };
    } finally { clearTimeout(timeout); }
  }
  async function readCart() {
    const { response, body } = await requestJson(route('cart.js'));
    if (!response.ok || !body || !Array.isArray(body.items)) throw new Error('cart-read-unavailable');
    return body;
  }
  const normalizedProperties = (properties) => Object.entries(properties || {}).filter(([, value]) => value !== '' && value != null).sort(([a], [b]) => a.localeCompare(b));
  const identityQuantity = (cart, item) => cart.items.reduce((total, line) => {
    const plan = String(line.selling_plan_allocation?.selling_plan?.id || '');
    return String(line.variant_id || line.id) === String(item.id) && plan === String(item.selling_plan || '') && JSON.stringify(normalizedProperties(line.properties)) === JSON.stringify(normalizedProperties(item.properties)) ? total + Number(line.quantity) : total;
  }, 0);
  const itemFor = (form, state) => {
    const data = new FormData(form);
    const properties = {};
    for (const [key, value] of data) {
      const match = key.match(/^properties\[(.*)\]$/);
      if (value instanceof File && value.size) throw new Error('native-file-required');
      const source = [...form.elements].find((element) => element.name === key);
      if (source?.hasAttribute('data-product-preorder') && !submitButtons(form).some((button) => button.dataset.preorder === 'true')) continue;
      if (match) properties[match[1]] = String(value);
    }
    return { id: state.variantId, quantity: state.quantity, ...(state.sellingPlanId ? { selling_plan: state.sellingPlanId } : {}), ...(Object.keys(properties).length ? { properties } : {}) };
  };
  async function revalidate(form) {
    const record = forms.get(form);
    const state = selection(form);
    if (!state.selectionComplete || !state.quantityValid || !record.config.handle) return { ok: false, reason: 'choose_variant' };
    const { response, body } = await requestJson(route(`products/${encodeURIComponent(record.config.handle)}.js`));
    const current = response.ok && body?.variants?.find((variant) => String(variant.id) === state.variantId);
    if (!current || String(body.id) !== String(record.config.productId)) return { ok: false, reason: 'unavailable' };
    const allocation = (current.selling_plan_allocations || []).find((entry) => String(entry.selling_plan_id || entry.selling_plan?.id) === String(state.sellingPlanId));
    if (state.sellingPlanId && !allocation || !state.sellingPlanId && (body.requires_selling_plan || current.requires_selling_plan)) return { ok: false, reason: 'unavailable' };
    const price = Number(state.sellingPlanId ? allocation.price : current.price);
    const rule = current.quantity_rule || {};
    const validQuantity = state.quantity >= Number(rule.min || 1) && state.quantity <= Number(rule.max || Infinity) && (state.quantity - Number(rule.min || 1)) % Number(rule.increment || 1) === 0;
    record.fresh = { id: state.variantId, variant: { ...current, ...(price !== state.unitPrice ? { unitHtml: '' } : {}) } };
    if (!current.available || !validQuantity) return { ok: false, reason: 'unavailable' };
    if (price !== state.unitPrice) return { ok: false, reason: 'changed' };
    return { ok: true, state: selection(form) };
  }
  function confirmedEvents(form, operation, cart, amount) {
    if (operation.emitted) return;
    operation.emitted = true;
    const record = forms.get(form);
    const line = cart?.items?.find((item) => String(item.variant_id) === String(operation.item.id));
    const item = { item_id: String(operation.item.id), item_name: record.config.title, item_variant: selection(form).variant?.title, price: operation.price / 100, quantity: amount, item_list_id: record.context?.dataset.auSource || form.dataset.avCartSource || 'pdp' };
    try {
      window.AVAnalytics?.standard?.('add_to_cart', item, {}, { dedupeKey: `au-purchase:${operation.id}`, ttl: 0 });
      const name = record.context?.dataset.auAnalyticsEvent;
      if (name) window.AVAnalytics?.legacy?.(name, { product_id: record.config.productId, variant_id: operation.item.id, quantity: amount, source: record.context.dataset.auSource });
      else if (form.closest('product-info')) window.AVAnalytics?.custom?.('au_pdp_add_to_cart', { source: 'pdp', product_id: record.config.productId, product_handle: record.config.handle, variant_id: operation.item.id, interaction_type: form.dataset.avCartSource === 'sticky' ? 'sticky_add' : 'primary_add' });
    } catch (_) { /* Analytics are optional and cannot repeat or invalidate a successful add. */ }
    safeEvent(form, 'australia:purchase_confirmed', { productId: record.config.productId, variantId: operation.item.id, quantity: amount, operationId: operation.id });
    if (cart) safeEvent(document, 'theme:cart:change', { cartCount: cart.item_count });
    if (line) safeEvent(form, 'theme:product:add', { response: { ...line, quantity: amount }, button: submitButtons(form)[0], cartType: window.theme?.settings?.cartType || 'page' });
  }
  async function reconcile(form, operation = forms.get(form)?.operation) {
    if (!operation) return { status: 'error', added: 0 };
    setStatus(form, 'reconciling');
    try {
      const cart = await readCart();
      if (cart.currency !== operation.before.currency) throw new Error('cart-currency-changed');
      const delta = identityQuantity(cart, operation.item) - identityQuantity(operation.before, operation.item);
      operation.cart = cart;
      if (delta === operation.item.quantity) {
        setStatus(form, 'confirmed', label(form, 'added'));
        confirmedEvents(form, operation, cart, delta);
        return { status: 'confirmed', added: delta, cart };
      }
      if (delta > 0 && delta < operation.item.quantity) {
        operation.partial = delta;
        confirmedEvents(form, operation, cart, delta);
        setStatus(form, 'unknown', `${label(form, 'partial')} ${delta} / ${operation.item.quantity}`);
        return { status: 'partial', added: delta, cart };
      }
      if (delta === 0 && operation.definiteError) {
        setStatus(form, 'error', operation.errorText || label(form, 'error'));
        return { status: 'error', added: 0, cart };
      }
      if (operation.confirmedResponse && delta === 0) {
        setStatus(form, 'confirmed', label(form, 'added'));
        confirmedEvents(form, operation, null, operation.item.quantity);
        return { status: 'confirmed', added: operation.item.quantity, cart: null, uiRecovery: true };
      }
      setStatus(form, 'unknown', label(form, 'unknown'));
      return { status: 'unknown', added: null, cart };
    } catch (_) {
      if (operation.confirmedResponse) {
        setStatus(form, 'confirmed', label(form, 'added'));
        confirmedEvents(form, operation, null, operation.item.quantity);
        return { status: 'confirmed', added: operation.item.quantity, uiRecovery: true };
      }
      setStatus(form, 'unknown', label(form, 'unknown'));
      return { status: 'unknown', added: null };
    }
  }
  async function add(form, { navigate = true, reviewToken = null } = {}) {
    register(form);
    const record = forms.get(form);
    if (!record || lockedStatuses.has(record.status) || activeOperation || activeReview && activeReview !== reviewToken) return { status: 'blocked', added: 0 };
    const initial = selection(form);
    if (!initial.available || !form.checkValidity()) {
      render(form, label(form, initial.selectionComplete ? 'unavailable' : 'choose_variant'));
      form.reportValidity(); return { status: 'unavailable', added: 0 };
    }
    const operation = { id: `${Date.now()}-${++sequence}`, form, emitted: false };
    try { operation.initialItem = itemFor(form, initial); } catch (_) { setStatus(form, 'error', label(form, 'error')); return { status: 'error', added: 0 }; }
    record.frozen = initial;
    const info = nativeInfo(form);
    const controls = [...form.elements, ...(info?.querySelectorAll('[data-quantity-plus], [data-quantity-minus], variant-selects') || [])];
    for (const control of controls) {
      if (control instanceof HTMLButtonElement && control.type === 'submit' || control.type === 'hidden') continue;
      record.frozenControls.set(control, Boolean(control.inert)); control.inert = true;
    }
    activeOperation = operation;
    setStatus(form, 'pending');
    try {
      const verified = await revalidate(form);
      if (!verified.ok) { setStatus(form, verified.reason === 'unavailable' ? 'unavailable' : 'idle', label(form, verified.reason)); return { status: verified.reason, added: 0 }; }
      const currentItem = itemFor(form, liveSelection(form));
      if (JSON.stringify(currentItem) !== JSON.stringify(operation.initialItem)) { setStatus(form, 'idle', label(form, 'changed')); return { status: 'changed', added: 0 }; }
      operation.item = operation.initialItem;
      operation.price = verified.state.unitPrice;
      operation.before = await readCart();
      if (record.config.currency && operation.before.currency !== record.config.currency) { setStatus(form, 'error', label(form, 'changed')); return { status: 'changed', added: 0 }; }
      record.operation = operation;
      if (JSON.stringify(itemFor(form, liveSelection(form))) !== JSON.stringify(operation.item)) { record.operation = null; setStatus(form, 'idle', label(form, 'changed')); return { status: 'changed', added: 0 }; }
      try {
        const { response, body } = await requestJson(route('cart/add.js'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [operation.item] }) });
        operation.definiteError = !response.ok && response.status >= 400 && response.status < 500;
        operation.errorText = typeof body?.description === 'string' ? body.description : typeof body?.message === 'string' ? body.message : '';
        const added = body?.items?.[0] || body;
        operation.confirmedResponse = Boolean(response.ok && added && String(added.variant_id || added.id) === String(operation.item.id));
      } catch (_) { operation.definiteError = false; }
      const result = await reconcile(form, operation);
      if (result.status === 'confirmed') {
        if (navigate) closeChooser();
        // Release 6.5: a confirmed add is announced. The side cart (on /cart, the cart section) shows it and cancels the jump to /cart; without it the page goes to /cart as before. Not for the tasting review, which adds several and goes to /cart itself.
        let shown = false;
        if (!reviewToken) {
          try { shown = !document.dispatchEvent(new CustomEvent('australia:purchase_added', { cancelable: true, detail: { form, productId: record.config.productId, handle: record.config.handle, variantId: operation.item.id, quantity: operation.item.quantity, source: record.context?.dataset.auSource || '', cart: result.cart || null } })); } catch (_) { shown = false; }
        }
        if (navigate && !shown) location.assign(route('cart'));
        // The page stays: once the side cart closes, the button can add again.
        if (shown) document.addEventListener('australia:sidecart_closed', () => { if (forms.get(form)?.status === 'confirmed') { forms.get(form).operation = null; setStatus(form, 'idle', ''); } }, { once: true });
      }
      return result;
    } catch (_) {
      if (record.operation === operation) return await reconcile(form, operation);
      setStatus(form, 'error', label(form, 'error'));
      return { status: 'error', added: 0 };
    } finally {
      if (!['unknown'].includes(record.status)) activeOperation = null;
    }
  }
  function closeChooser() {
    if (!openChooser) return;
    const owner = openChooser;
    openChooser = null;
    if (owner.modal?.open) owner.modalClose();
  }
  function enhanceChooser(owner, context) {
    if (owner.dataset.auReady || typeof owner.renderModal !== 'function') return;
    owner.dataset.auReady = 'true';
    const originalClose = owner.modalClose.bind(owner);
    owner.modalClose = () => {
      const form = owner.modal?.querySelector('form[data-type="add-to-cart-form"], form[data-product-form]') || [...owner.modal?.querySelectorAll('form') || []].find((item) => item.querySelector('[data-add-to-cart]'));
      if (form) { contexts.set(context, { ...contexts.get(context), form, saved: publicState(selection(form)) }); }
      owner.isAnimating = false;
      owner.modal?.setAttribute('closing', '');
      originalClose();
      if (openChooser === owner) openChooser = null;
      owner.modalButton?.focus({ preventScroll: true });
    };
    owner.renderModal = async () => {
      try {
        const saved = contexts.get(context)?.saved;
        const url = new URL(route(`products/${encodeURIComponent(owner.handle)}`));
        url.searchParams.set('section_id', 'api-product-upsell');
        if (saved?.variantId) url.searchParams.set('variant', saved.variantId);
        const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('chooser-unavailable');
        owner.modalCreate(await response.text());
        const dialog = owner.modal;
        if (!dialog) throw new Error('chooser-unavailable');
        dialog.dataset.auPurchaseModal = '';
        if (dialog.open) dialog.close();
        dialog.showModal();
        dialog.setAttribute('aria-label', config(context).title);
        openChooser = owner;
        const form = dialog.querySelector('form[data-type="add-to-cart-form"], form[data-product-form]') || [...dialog.querySelectorAll('form')].find((item) => item.querySelector('[data-add-to-cart]'));
        if (!form) throw new Error('chooser-form-unavailable');
        register(form, context);
        const nativeQuantity = [...form.elements].find((input) => input.name === 'quantity');
        if (nativeQuantity?.type === 'hidden') {
          nativeQuantity.type = 'number';
          const rule = selection(form).variant?.quantity_rule || {};
          nativeQuantity.min = rule.min || 1; nativeQuantity.step = rule.increment || 1;
          if (rule.max) nativeQuantity.max = rule.max;
          const quantityLabel = document.createElement('label'); quantityLabel.className = 'au-purchase__quantity';
          const caption = document.createElement('span'); caption.textContent = label(form, 'quantity');
          quantityLabel.append(caption); form.prepend(quantityLabel); quantityLabel.append(nativeQuantity);
        }
        if (!saved?.variantId) {
          const record = forms.get(form);
          const picker = dialog.querySelector('variant-selects');
          const positions = new Set([...picker?.querySelectorAll('[data-option-position]') || []].map((node) => node.dataset.optionPosition));
          record.requiresChoice = positions.size > 0;
          const touched = new Set();
          const recordChoice = (event) => {
            const currentPicker = dialog.querySelector('variant-selects');
            const choice = event.target.closest('input[type="radio"], [data-popout-option], [data-popout-input], select');
            const wrapper = choice?.closest('[data-option-position]');
            if (!wrapper || !currentPicker?.contains(choice)) return;
            const selected = json(currentPicker.querySelector('[data-selected-variant]'));
            const value = choice.value || choice.dataset.value;
            record.pendingVariant = String(selected.options?.[Number(wrapper.dataset.optionPosition) - 1]) !== String(value);
            touched.add(wrapper.dataset.optionPosition);
            record.requiresChoice = touched.size < positions.size;
            render(form, record.requiresChoice ? label(form, 'choose_variant') : '');
          };
          dialog.addEventListener('click', recordChoice);
          dialog.addEventListener('change', recordChoice);
        }
        const quantity = [...form.elements].find((input) => input.name === 'quantity');
        if (quantity && saved?.quantity) { quantity.value = saved.quantity; quantity.dispatchEvent(new Event('change', { bubbles: true })); }
        const actions = form.querySelector('[data-add-to-cart]')?.closest('.product__submit');
        if (actions) actions.dataset.auPurchaseModalActions = '';
        dialog.addEventListener('cancel', (event) => { event.preventDefault(); owner.modalClose(); });
        window.theme?.a11y?.trapFocus?.(dialog);
        dialog.querySelector('[data-quick-add-modal-close]')?.focus();
        render(form);
      } catch (_) {
        if (owner.modal?.open) owner.modalClose();
        owner.modalButton.disabled = false;
        owner.modalButton.classList.remove('is-loading', 'loading');
        owner.quickAddHolder?.classList.remove('is-disabled');
        setText(context.querySelector('[data-au-purchase-status]'), config(context).labels?.error);
        context.querySelector('[data-au-purchase-fallback]')?.removeAttribute('hidden');
      }
    };
    const button = context.querySelector('[data-au-purchase-choose]');
    button.hidden = false;
    context.querySelector('[data-au-purchase-fallback]')?.setAttribute('hidden', '');
  }
  const contextForm = (context) => context?.querySelector('form[data-au-purchase-form]') || contexts.get(context)?.form;
  // View ownership only: selected items, prices and request state remain in the
  // existing native forms and coordinator. The original review opener is reused.
  const tastingRailViews = new Map();
  let activeTastingRail = null;
  const refreshTastingRail = (scope) => tastingRailViews.get(scope)?.feedback();
  function mountTastingRail(scope) {
    if (tastingRailViews.has(scope)) return;
    const rail = scope.querySelector('[data-au-tasting-controls]');
    const slot = scope.querySelector('[data-au-tasting-controls-slot]');
    const zone = scope.querySelector('[data-spoon-panels]');
    if (!rail || !slot || !zone) return;
    const abort = new AbortController(), signal = abort.signal;
    const viewport = window.visualViewport, mobile = matchMedia('(max-width: 767px)');
    const blockers = 'dialog[open], [aria-modal="true"], cart-drawer.is-open, [data-av-mobile-drawer], #shopify-pc__banner, #shopify-privacy__banner-div, [data-av-cookie-banner], [data-av-consent-banner]';
    const footer = document.querySelector('footer.footer-sections');
    const summary = rail.querySelector('[data-au-tasting-summary]');
    const lines = rail.querySelector('[data-au-tasting-summary-lines]');
    const totalRow = rail.querySelector('[data-au-tasting-summary-total-row]');
    const total = rail.querySelector('[data-au-tasting-summary-total]');
    const status = rail.querySelector('[data-au-tasting-summary-status]');
    const cart = rail.querySelector('[data-au-tasting-summary-cart]');
    if (!summary || !lines || !totalRow || !total || !status || !cart) return;
    const originalBlockSize = slot.style.getPropertyValue('block-size');
    const originalPriority = slot.style.getPropertyPriority('block-size');
    let frame = 0, focusFrame = 0, disposed = false, inlineHeight = 0, railHeight = 0;
    let watchAbort = null, intersection = null, resize = null, mutations = null, additions = null;
    let screenHeight = innerHeight, screenWidth = innerWidth;
    const fixed = () => rail.dataset.auTastingFixed === 'true';
    const restoreSlot = () => {
      if (originalBlockSize) slot.style.setProperty('block-size', originalBlockSize, originalPriority);
      else slot.style.removeProperty('block-size');
    };
    const unfix = () => { if (fixed()) { delete rail.dataset.auTastingFixed; restoreSlot(); } };
    const visible = (node) => {
      if (!node?.isConnected || node.closest('[hidden], [inert], [aria-hidden="true"]')) return false;
      const box = node.getBoundingClientRect(), style = getComputedStyle(node);
      const top = viewport?.offsetTop || 0, bottom = top + (viewport?.height || innerHeight);
      return box.width > 0 && box.height > 0 && box.bottom > top && box.top < bottom && style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) > 0;
    };
    const keyboard = () => {
      if (Math.abs(innerWidth - screenWidth) > 80) { screenWidth = innerWidth; screenHeight = innerHeight; }
      if (!document.activeElement?.matches('input:not([type="hidden"]), textarea, select, [contenteditable="true"]')) { screenHeight = Math.max(screenHeight, innerHeight); return false; }
      return !(viewport?.scale > 1.1) && screenHeight - (viewport?.height || innerHeight) > 140;
    };
    function geometry() {
      frame = 0;
      if (disposed || !scope.isConnected) return;
      // The no-selection/default route never reads layout or observes the page.
      if (!watchAbort || activeTastingRail !== scope || !mobile.matches) { unfix(); return; }
      let show = false, measuredInline = inlineHeight;
      // Read before changing position or reservation. Fixed-mode size must never
      // replace the last inline slot size, including after selected text grows.
      if (!fixed()) measuredInline = slot.getBoundingClientRect().height;
      if (scope.querySelector('[data-au-tasting-review]')?.hidden === true) {
        const box = zone.getBoundingClientRect(), top = viewport?.offsetTop || 0;
        const blocked = Boolean(scope.closest('[inert], [aria-hidden="true"]')) || document.documentElement.hasAttribute('data-scroll-locked') || document.body.classList.contains('av49-sensitive-active') || [...document.querySelectorAll(blockers)].some(visible);
        const competing = [...document.querySelectorAll('[data-av-sensory-floatbar], [data-avs-sticky]')].some(visible);
        const focused = document.activeElement;
        const focusBox = focused instanceof Element && focused !== document.body && focused !== document.documentElement && !rail.contains(focused) ? focused.getBoundingClientRect() : null;
        const bottom = top + (viewport?.height || innerHeight);
        const obscuresFocus = focusBox && focusBox.height > 0 && focusBox.bottom > bottom - (railHeight || measuredInline) && focusBox.top < bottom && focusBox.bottom > top;
        show = box.height > 0 && box.bottom <= top && !blocked && !competing && !keyboard() && !visible(footer) && !obscuresFocus;
      }
      if (Number.isFinite(measuredInline) && measuredInline > 0) inlineHeight = measuredInline;
      if (show && inlineHeight > 0) {
        if (!fixed()) { slot.style.setProperty('block-size', `${inlineHeight}px`); rail.dataset.auTastingFixed = 'true'; }
      } else unfix();
    }
    const schedule = () => { if (!disposed && watchAbort && activeTastingRail === scope && !frame) frame = requestAnimationFrame(geometry); };
    function feedback() {
      if (disposed) return;
      if (summary.hidden && !scope.querySelector('[data-au-tasting-select]:checked')) return;
      const entries = tastingItems(scope), labels = config(scope.querySelector('[data-au-purchase]')).labels || {};
      while (lines.children.length > entries.length) lines.lastElementChild.remove();
      entries.forEach((entry, index) => {
        const line = lines.children[index] || lines.appendChild(document.createElement('li'));
        const title = config(entry.context).title || '';
        const format = entry.state?.variant?.format || '';
        const quantity = Number.isSafeInteger(entry.state?.quantity) && entry.state.quantity > 0 ? ` × ${entry.state.quantity}` : '';
        setText(line, `${title}${format ? ` · ${format}` : ''}${quantity}`);
      });
      const states = entries.map(entry => entry.state);
      const unknown = states.some(state => state?.status === 'unknown');
      const pending = states.some(state => state?.status === 'pending');
      const reconciling = states.some(state => state?.status === 'reconciling');
      const confirmed = states.some(state => state?.status === 'confirmed');
      const incomplete = states.some(state => !state?.selectionComplete || !state.quantityValid);
      const unavailable = states.some(state => !state?.available);
      const complete = entries.length > 0 && states.every(state => state?.available && !state.locked && state.selectionComplete && Number.isSafeInteger(state.totalPrice) && state.totalPrice >= 0);
      const message = unknown ? labels.unknown : pending ? labels.pending : reconciling ? labels.reconciling : confirmed ? labels.added : incomplete ? labels.choose_variant : unavailable ? labels.unavailable : '';
      setText(status, message || '');
      setProperty(status, 'hidden', !message);
      setText(total, complete ? money(states.reduce((sum, state) => sum + state.totalPrice, 0)) : '');
      setProperty(totalRow, 'hidden', !complete);
      setProperty(cart, 'hidden', !unknown);
      setProperty(summary, 'hidden', entries.length === 0);
      if (!entries.length && activeTastingRail === scope) { activeTastingRail = null; suspend(); }
      schedule();
    }
    const choose = (event) => {
      if (event.target.matches('[data-au-tasting-select]') && event.target.checked) {
        const previous = activeTastingRail;
        activeTastingRail = scope;
        if (previous && previous !== scope) tastingRailViews.get(previous)?.suspend();
        watch();
      }
      feedback();
    };
    scope.addEventListener('change', choose, { signal });
    scope.addEventListener('australia:purchase_state', feedback, { signal });
    scope.addEventListener('click', schedule, { signal });
    function watch() {
      if (watchAbort || disposed) return;
      watchAbort = new AbortController();
      const signal = watchAbort.signal;
      for (const event of ['scroll', 'resize', 'pageshow']) window.addEventListener(event, schedule, { passive: true, signal });
      for (const event of ['focusin', 'focusout', 'toggle', 'theme:cart-drawer:open', 'theme:cart-drawer:close', 'australia:purchase_chooser_opening']) document.addEventListener(event, schedule, { capture: true, signal });
      const overlay = (event) => { if (event.detail?.open === true) unfix(); schedule(); };
      document.addEventListener('australia:overlay_changed', overlay, { capture: true, signal });
      const motionEnd = (event) => { if (event.target instanceof Element && (event.target.matches(blockers) || event.target.closest(blockers))) schedule(); };
      for (const event of ['transitionend', 'animationend']) document.addEventListener(event, motionEnd, { capture: true, signal });
      viewport?.addEventListener('resize', schedule, { passive: true, signal });
      viewport?.addEventListener('scroll', schedule, { passive: true, signal });
      mobile.addEventListener('change', schedule, { signal });
      intersection = typeof IntersectionObserver === 'function' ? new IntersectionObserver(schedule) : null;
      intersection?.observe(zone);if (footer) intersection?.observe(footer);
      resize = typeof ResizeObserver === 'function' ? new ResizeObserver(entries => {
        for (const entry of entries) {
          const size = entry.borderBoxSize;
          const height = (Array.isArray(size) ? size[0] : size)?.blockSize;
          if (!Number.isFinite(height)) continue;
          if (entry.target === slot && !fixed() && height > 0) inlineHeight = height;
          if (entry.target === rail) railHeight = height;
        }
        schedule();
      }) : null;
      resize?.observe(slot, { box: 'border-box' });resize?.observe(rail, { box: 'border-box' });
      const observed = new WeakSet();
      const observedRoots = `${blockers}, [data-av-sensory-floatbar], [data-avs-sticky]`;
      const observeRoot = node => {
        if (!mutations || observed.has(node)) return;
        observed.add(node);
        mutations.observe(node, { attributes: true, attributeFilter: ['open', 'hidden', 'aria-hidden', 'aria-modal', 'inert', 'class', 'style', 'data-scroll-locked'] });
      };
      mutations = new MutationObserver(schedule);
      for (let node = scope; node; node = node.parentElement) observeRoot(node);
      document.querySelectorAll(observedRoots).forEach(observeRoot);
      // Only inserted/removed blocker roots need discovery. Attribute observation
      // is confined to actual blockers and this scope's ancestors, never a subtree.
      additions = new MutationObserver(records => {
        let relevant = false;
        for (const record of records) for (const node of [...record.addedNodes, ...record.removedNodes]) {
          if (!(node instanceof Element)) continue;
          const roots = [...node.querySelectorAll(observedRoots)];
          if (node.matches(observedRoots)) roots.push(node);
          if (roots.length) { relevant = true; roots.filter(root => root.isConnected).forEach(observeRoot); }
        }
        if (relevant) schedule();
      });
      additions.observe(document.body, { childList: true, subtree: true });
    }
    function suspend() {
      watchAbort?.abort();watchAbort = null;cancelAnimationFrame(frame);frame = 0;
      intersection?.disconnect();resize?.disconnect();mutations?.disconnect();additions?.disconnect();
      intersection = resize = mutations = additions = null;
      unfix();
    }
    function restoreFocus() {
      const originalFocus = document.activeElement;
      schedule();cancelAnimationFrame(focusFrame);
      focusFrame = requestAnimationFrame(() => {
        focusFrame = 0;
        if (!disposed && scope.isConnected && document.activeElement === originalFocus) scope.auReviewOpener?.focus({ preventScroll: true });
      });
    }
    const dispose = () => {
      disposed = true;abort.abort();suspend();cancelAnimationFrame(focusFrame);restoreSlot();
      if (activeTastingRail === scope) activeTastingRail = null;
      tastingRailViews.delete(scope);
    };
    tastingRailViews.set(scope, { feedback, suspend, restoreFocus, dispose });
    feedback();
  }
  function tastingItems(scope) {
    return [...scope.querySelectorAll('[data-au-tasting-item]')].filter((item) => item.querySelector('[data-au-tasting-select]')?.checked).map((item) => {
      const context = item.querySelector('[data-au-purchase]');
      const form = contextForm(context);
      if (form) register(form, context);
      return { item, context, form, state: form ? selection(form) : null };
    });
  }
  const reviewFocusFrames = new WeakMap();
  const focusReviewAfterLayout = (scope, resolveTarget, scrollTarget = false) => {
    const previous = reviewFocusFrames.get(scope);
    if (previous) cancelAnimationFrame(previous);
    const originalFocus = document.activeElement;
    // Let the browser lay out the changed review before requesting focus.
    // Preserve a customer's intervening focus move and superseding requests.
    const first = requestAnimationFrame(() => {
      const second = requestAnimationFrame(() => {
        reviewFocusFrames.delete(scope);
        if (!scope.isConnected || scope.querySelector('[data-au-tasting-review]')?.hidden || document.activeElement !== originalFocus) return;
        const target = resolveTarget();
        if (scrollTarget) target?.scrollIntoView({ block: 'start', behavior: 'instant' });
        target?.focus({ preventScroll: true });
      });
      reviewFocusFrames.set(scope, second);
    });
    reviewFocusFrames.set(scope, first);
  };
  const reviewRowSources = new WeakMap();
  const reviewIdentity = entry => JSON.stringify([String(config(entry.context).productId), String(entry.state?.variantId || ''), String(entry.state?.sellingPlanId || '')]);
  function review(scope, { quantityOnly = false } = {}) {
    const entries = tastingItems(scope);
    const panel = scope.querySelector('[data-au-tasting-review]');
    const lines = scope.querySelector('[data-au-tasting-lines]');
    const configLabels = config(scope.querySelector('[data-au-purchase]')).labels || {};
    if (!panel || !lines) return false;
    const existingRows = [...lines.children];
    const preservesSelection = quantityOnly && existingRows.length === entries.length && entries.every((entry, index) => {
      const previous = reviewRowSources.get(existingRows[index]);
      const nativeQuantity = entry.form && [...entry.form.elements].find(element => element.name === 'quantity');
      return previous && previous.identity === reviewIdentity(entry) && previous.form === entry.form && previous.input === nativeQuantity;
    });
    if (preservesSelection) {
      let total = 0, complete = entries.length > 0;
      entries.forEach((entry, index) => {
        const row = existingRows[index], settings = config(entry.context);
        const valid = entry.state?.available && !entry.state.locked;
        setText(row.children[0], `${settings.title}${entry.state?.variant?.format ? ` · ${entry.state.variant.format}` : ''}${entry.state?.quantity ? ` × ${entry.state.quantity}` : ''}`);
        setText(row.children[1], valid ? money(entry.state.totalPrice) : configLabels.choose_variant || '');
        if (valid) total += entry.state.totalPrice; else complete = false;
        const quantity = row.querySelector('[data-au-review-quantity]'), nativeQuantity = reviewRowSources.get(row).input;
        if (quantity && entry.state) {
          if (quantity.value !== String(entry.state.quantity)) quantity.value = entry.state.quantity;
          setAttribute(quantity, 'min', nativeQuantity?.min || entry.state.variant?.quantity_rule?.min || 1);
          setAttribute(quantity, 'step', nativeQuantity?.step || entry.state.variant?.quantity_rule?.increment || 1);
          if (nativeQuantity?.max) setAttribute(quantity, 'max', nativeQuantity.max); else if (quantity.hasAttribute('max')) quantity.removeAttribute('max');
          setProperty(quantity, 'disabled', entry.state.locked);
        }
      });
      setText(scope.querySelector('[data-au-tasting-total]'), money(total));
      setText(scope.querySelector('[data-au-tasting-status]'), complete ? '' : entries.length ? configLabels.choose_variant : configLabels.empty);
      const confirm = scope.querySelector('[data-au-tasting-confirm]');
      setProperty(confirm, 'disabled', !complete || Boolean(scope.dataset.auTastingBusy));
      setProperty(panel, 'hidden', false);
      refreshTastingRail(scope);
      return complete;
    }
    const fragment = document.createDocumentFragment();
    let total = 0;
    let complete = entries.length > 0;
    for (const entry of entries) {
      const row = document.createElement('li');
      const title = document.createElement('span');
      const price = document.createElement('strong');
      const remove = document.createElement('button');
      const quantity = document.createElement('input');
      const settings = config(entry.context);
      title.textContent = `${settings.title}${entry.state?.variant?.format ? ` · ${entry.state.variant.format}` : ''}${entry.state?.quantity ? ` × ${entry.state.quantity}` : ''}`;
      const valid = entry.state?.available && !entry.state.locked;
      price.textContent = valid ? money(entry.state.totalPrice) : configLabels.choose_variant || '';
      if (valid) total += entry.state.totalPrice;
      else complete = false;
      remove.type = 'button'; remove.textContent = configLabels.remove || ''; remove.setAttribute('aria-label', `${configLabels.remove || ''} ${settings.title}`);
      remove.addEventListener('click', () => {
        const index = [...lines.children].indexOf(row);
        entry.item.querySelector('[data-au-tasting-select]').checked = false; review(scope);
        focusReviewAfterLayout(scope, () => lines.children[Math.min(index, lines.children.length - 1)]?.querySelector('input,button') || panel);
      });
      if (entry.form && entry.state) {
        const input = [...entry.form.elements].find((element) => element.name === 'quantity');
        quantity.type = 'number'; quantity.value = entry.state.quantity; quantity.min = input?.min || entry.state.variant?.quantity_rule?.min || 1; quantity.step = input?.step || entry.state.variant?.quantity_rule?.increment || 1;
        if (input?.max) quantity.max = input.max;
        quantity.disabled = entry.state.locked;
        quantity.setAttribute('aria-label', `${configLabels.quantity || ''} ${settings.title}`);
        quantity.dataset.auReviewQuantity = String(settings.productId);
        quantity.addEventListener('change', (event) => {
          event.stopPropagation();
          if (!input || !quantity.checkValidity()) { quantity.reportValidity(); return; }
          input.value = quantity.value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
          if (!quantity.isConnected) focusReviewAfterLayout(scope, () => scope.querySelector(`[data-au-review-quantity="${settings.productId}"]`));
        });
      }
      reviewRowSources.set(row, { identity: reviewIdentity(entry), form: entry.form, input: entry.form && [...entry.form.elements].find(element => element.name === 'quantity') });
      row.append(title, price, quantity, remove); fragment.append(row);
    }
    lines.replaceChildren(fragment);
    setText(scope.querySelector('[data-au-tasting-total]'), money(total));
    setText(scope.querySelector('[data-au-tasting-status]'), complete ? '' : entries.length ? configLabels.choose_variant : configLabels.empty);
    const confirm = scope.querySelector('[data-au-tasting-confirm]');
    if (confirm) confirm.disabled = !complete || Boolean(scope.dataset.auTastingBusy);
    panel.hidden = false;
    refreshTastingRail(scope);
    return complete;
  }
  async function confirmTasting(scope) {
    if (scope.dataset.auTastingBusy || activeOperation || activeReview || !review(scope)) return;
    const reviewToken = {};
    activeReview = reviewToken;
    scope.dataset.auTastingBusy = 'true';
    const button = scope.querySelector('[data-au-tasting-confirm]');
    button.disabled = true; button.setAttribute('aria-busy', 'true');
    const results = [];
    try {
      const entries = tastingItems(scope);
      for (const entry of entries) {
        let checked;
        try { checked = await revalidate(entry.form); } catch (_) { checked = { ok: false, reason: 'error' }; }
        if (!checked.ok) {
          setStatus(entry.form, checked.reason === 'unavailable' ? 'unavailable' : 'idle', label(entry.form, checked.reason));
          review(scope);
          setText(scope.querySelector('[data-au-tasting-status]'), label(entry.form, checked.reason));
          return;
        }
      }
      for (const entry of entries) {
        if (activeOperation) break;
        const result = await add(entry.form, { navigate: false, reviewToken });
        results.push({ productId: config(entry.context).productId, variantId: entry.state.variantId, requested: entry.state.quantity, ...result });
        const checkbox = entry.item.querySelector('[data-au-tasting-select]');
        if (result.status === 'confirmed') checkbox.checked = false;
        if (['unknown', 'partial'].includes(result.status)) break;
      }
      const labels = config(scope.querySelector('[data-au-purchase]')).labels || {};
      review(scope);
      const summary = results.map((result) => {
        const entry = entries.find((item) => String(config(item.context).productId) === String(result.productId));
        const name = config(entry?.context).title || '';
        const amount = result.added == null ? '' : `${result.added} / ${result.requested}. `;
        const outcome = result.status === 'confirmed' ? labels.added
          : result.status === 'unknown' ? labels.unknown
            : result.status === 'partial' ? labels.partial
              : forms.get(entry?.form)?.operation?.errorText || labels[result.status] || labels.error;
        return `${name}. ${amount}${outcome || ''}`;
      }).join('. ');
      setText(scope.querySelector('[data-au-tasting-status]'), summary);
      const cartLink = scope.querySelector('[data-au-tasting-cart]');
      if (cartLink) cartLink.hidden = false;
      safeEvent(scope, 'australia:purchase_review_result', { items: results.map(({ productId, variantId, requested, status, added }) => ({ productId, variantId, requested, status, added })) });
      if (results.length === entries.length && results.every((result) => result.status === 'confirmed')) { closeChooser(); location.assign(route('cart')); }
    } finally {
      activeReview = null;
      delete scope.dataset.auTastingBusy;
      button.setAttribute('aria-busy', 'false');
      button.disabled = Boolean(activeOperation) || !tastingItems(scope).some((entry) => entry.state?.available && !entry.state.locked);
    }
  }
  function init(scope = document) {
    scope.querySelectorAll?.('[data-au-purchase-native]').forEach((info) => {
      const form = document.getElementById(info.dataset.auPurchaseFormId);
      if (form && info.contains(form)) register(form);
    });
    scope.querySelectorAll?.('[data-au-purchase-form]').forEach((form) => register(form));
    scope.querySelectorAll?.('quick-add-product[data-au-purchase-chooser]').forEach((owner) => enhanceChooser(owner, owner.closest('[data-au-purchase]')));
    scope.querySelectorAll?.('[data-au-tasting]').forEach((reviewScope) => {
      if (!reviewScope.querySelector('[data-au-purchase]')) return;
      reviewScope.querySelectorAll('[data-au-tasting-choice], [data-au-tasting-controls]').forEach((node) => { node.hidden = false; });
      mountTastingRail(reviewScope);
    });
  }
  window.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-au-purchase-form')) return;
    register(form);
    event.preventDefault(); event.stopImmediatePropagation();
    void add(form);
  }, true);
  document.addEventListener('click', (event) => {
    const choose = event.target.closest('[data-au-purchase-choose]');
    if (choose) {
      if (activeOperation || document.querySelector('dialog[open], cart-drawer[open], [data-cart-drawer].is-open')) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      safeEvent(document, 'australia:purchase_chooser_opening', { productId: config(choose.closest('[data-au-purchase]')).productId });
    }
    const check = event.target.closest('[data-au-purchase-reconcile]');
    if (check) {
      const form = contextForm(check.closest('[data-au-purchase]')) || check.closest('form');
      if (!form || forms.get(form)?.status === 'reconciling') return;
      void reconcile(form).then((result) => { if (result.status === 'confirmed' || result.status === 'error') activeOperation = null; });
    }
    const scope = event.target.closest('[data-au-tasting]');
    if (!scope) return;
    if (event.target.closest('[data-au-tasting-open]')) {
      scope.auReviewOpener = event.target.closest('[data-au-tasting-open]');
      const fromFixedRail = scope.auReviewOpener.closest('[data-au-tasting-fixed="true"]') !== null;
      review(scope);
      focusReviewAfterLayout(scope, () => scope.querySelector('[data-au-tasting-review]'), fromFixedRail);
    }
    if (event.target.closest('[data-au-tasting-confirm]')) void confirmTasting(scope);
    if (event.target.closest('[data-au-tasting-close]')) {
      scope.querySelector('[data-au-tasting-review]').hidden = true;
      const view = tastingRailViews.get(scope);
      if (view) view.restoreFocus(); else scope.auReviewOpener?.focus({ preventScroll: true });
    }
    if (event.target.closest('[data-au-tasting-remove]')) { const input = event.target.closest('[data-au-tasting-item]')?.querySelector('[data-au-tasting-select]'); if (input) input.checked = false; review(scope); }
  }, true);
  document.addEventListener('change', (event) => {
    const associated = event.target.form;
    if (associated && forms.has(associated) && !associated.contains(event.target)) {
      if (lockedStatuses.has(forms.get(associated).status)) render(associated);
      else setStatus(associated, 'idle', '');
    }
    const scope = event.target.closest('[data-au-tasting]');
    if (scope && (event.target.matches('[data-au-tasting-select]') || associated && forms.has(associated)) && !scope.querySelector('[data-au-tasting-review]')?.hidden && !scope.dataset.auTastingBusy) review(scope, { quantityOnly: event.target.name === 'quantity' });
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('theme:product-info:loaded', () => init(document));
  document.addEventListener('theme:cart:open', closeChooser);
  document.addEventListener('shopify:section:unload', (event) => {
    for (const [scope, view] of tastingRailViews) if (event.target.contains(scope)) view.dispose();
    for (const form of registered) if (event.target.contains(form) && !lockedStatuses.has(forms.get(form)?.status)) { forms.get(form)?.observer?.disconnect(); registered.delete(form); }
    if (openChooser && event.target.contains(openChooser)) closeChooser();
  });
  window.AUPurchase = Object.freeze({ version: '1.0', register, getState: (form) => { if (!forms.has(form)) register(form); return forms.has(form) ? publicState(selection(form)) : null; }, add, reconcile, review, confirmReview: confirmTasting, closeChooser, formatMoney: money, init });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
  safeEvent(document, 'australia:purchase_ready', { version: '1.0' });
})();
